import * as React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, BookOpen, CheckCircle2 } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderDescription,
} from "@eduviaos/ui-kit";
import { useCatalogueReadiness, readinessGateMeta } from "@/hooks/useCatalogueReadiness";

/**
 * Phase H4 FE-2 — `<PrerequisiteGate>` wraps a module landing page
 * and surfaces a "Configure prerequisites" screen when one or more
 * catalogue gates are unmet.
 *
 * Why a separate gate component instead of relying purely on the
 * sidebar disabled-state: a user can land on a gated route via
 * deep link, browser back/forward, or a stale dashboard link the
 * admin clicked while we were still loading the readiness payload.
 * The gate is the server-side-style "you can't enter yet" hard
 * stop, the disabled sidebar entry is the soft visual cue.
 *
 * Loading state: when the readiness query is in flight we render
 * the children optimistically — the gate is for the steady state.
 * Flashing the configure UI for 80ms while the query resolves is
 * worse than letting the user see the (empty) module shell for
 * the same window; the module's own empty state covers that case.
 *
 * 404 READINESS_NO_BRANCH: surfaced as "Finish onboarding first"
 * with a Link to `/first-run/welcome`. Phase C1a ensures admins
 * with no first-run state are bounced there automatically; this
 * branch only ever fires for super-admins poking around a freshly
 * minted tenant via subdomain login.
 */

export type GatedModule = "timetable" | "attendance" | "substitution";

interface PrerequisiteGateProps {
  module: GatedModule;
  children: React.ReactNode;
}

const MODULE_DISPLAY: Record<GatedModule, { label: string; description: string }> = {
  timetable: {
    label: "Timetable",
    description: "AI-powered weekly schedule generation, conflict detection, manual overrides.",
  },
  attendance: {
    label: "Attendance",
    description:
      "Daily roll-call for students and staff, biometric / QR / manual capture, parent notifications.",
  },
  substitution: {
    label: "Substitution",
    description:
      "Suggest qualified substitutes when a teacher is absent, honour eligibility + load.",
  },
};

export function PrerequisiteGate({ module, children }: PrerequisiteGateProps) {
  const query = useCatalogueReadiness();

  if (query.isPending) {
    return <>{children}</>;
  }

  if (query.isError) {
    const err = query.error;
    // The BE error envelope (`app/shared/errors.py`) puts the
    // discriminator at the TOP level — `{ error: { code: "READINESS_NO_BRANCH",
    // details: { tenant_id, reason } } }` — so we match on `err.code`
    // (mapped by `ApiError.fromResponse` from `envelope.error.code`).
    // We also accept the discriminator inside `details.code` as
    // forward-compat in case a future BE refactor moves it, plus the
    // generic `"NOT_FOUND"` fallback for very old error envelopes.
    // Iron Rule #14 stage verification (May 2026) caught the original
    // `details.code` lookup as a silent no-op: the gate never fired,
    // so `/timetable` and `/attendance` rendered their normal shells
    // on a freshly-onboarded tenant instead of bouncing the admin to
    // the first-run wizard. See phase-H §H6a FE-hotfix.
    const detailsCode = (err.details as { code?: unknown } | undefined)?.code;
    if (
      err.status === 404 &&
      (err.code === "READINESS_NO_BRANCH" ||
        detailsCode === "READINESS_NO_BRANCH" ||
        err.code === "NOT_FOUND")
    ) {
      return <NoBranchPrerequisite />;
    }
    return <>{children}</>;
  }

  const view = query.data[module];
  if (view.ready) {
    return <>{children}</>;
  }

  return <PrerequisiteLanding module={module} missing={view.missing} counts={view.counts} />;
}

function PrerequisiteLanding({
  module,
  missing,
  counts,
}: {
  module: GatedModule;
  missing: string[];
  counts: Record<string, number>;
}) {
  const display = MODULE_DISPLAY[module];
  return (
    <div className="mx-auto max-w-3xl py-8">
      <PageHeader>
        <PageHeaderHeading>
          <PageHeaderTitle>{display.label}</PageHeaderTitle>
          <PageHeaderDescription>{display.description}</PageHeaderDescription>
        </PageHeaderHeading>
      </PageHeader>

      <Card className="border-amber-500/40 bg-amber-500/5">
        <CardHeader className="flex-col items-start gap-2 pb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle
              className="size-5 text-amber-600 dark:text-amber-400"
              aria-hidden="true"
            />
            <CardTitle className="text-base">Configure prerequisites first</CardTitle>
          </div>
          <CardDescription>
            {display.label} unlocks once the academic catalogue is populated. {missing.length} of
            the required pieces still need attention:
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <ul className="divide-border/60 divide-y">
            {missing.map((code) => {
              const meta = readinessGateMeta(code);
              const count = counts[code] ?? counts[`${code}s`];
              return (
                <li key={code} className="flex items-start gap-3 py-3">
                  <span
                    aria-hidden="true"
                    className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400"
                  >
                    <AlertTriangle className="size-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-tight">{meta.label}</p>
                    <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
                      {meta.description}
                      {typeof count === "number" ? (
                        <>
                          {" "}
                          <span className="font-mono">({count} configured)</span>
                        </>
                      ) : null}
                    </p>
                  </div>
                  <Button asChild variant="outline" size="sm" className="shrink-0">
                    <Link to={meta.fixHref}>
                      {meta.fixLabel}
                      <ArrowRight className="size-3.5" aria-hidden="true" />
                    </Link>
                  </Button>
                </li>
              );
            })}
          </ul>

          <div className="border-border bg-background/50 mt-4 flex items-start gap-3 rounded-lg border p-3 text-xs">
            <BookOpen className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <p className="text-muted-foreground leading-relaxed">
              The catalogue lives in the sidebar under{" "}
              <span className="text-foreground">Other</span> &rarr;{" "}
              <span className="text-foreground">Catalogue</span>. Once all prerequisites are green,
              this page automatically swaps to the live {display.label.toLowerCase()} workspace
              &mdash; no refresh needed.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="text-muted-foreground mt-3 inline-flex items-center gap-1.5 text-xs">
        <CheckCircle2 className="size-3.5 text-emerald-500" aria-hidden="true" />
        Already configured? The page will refresh automatically.
      </div>
    </div>
  );
}

function NoBranchPrerequisite() {
  return (
    <div className="mx-auto max-w-2xl py-12">
      <Card className="border-amber-500/40 bg-amber-500/5">
        <CardHeader className="flex-col items-start gap-2">
          <CardTitle>Finish onboarding first</CardTitle>
          <CardDescription>
            Your tenant has no branches yet. Once you complete the first-run wizard the academic
            catalogue and module gates will unlock.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild>
            <Link to="/first-run/welcome">
              Open onboarding wizard
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
